/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';

import { zIndex } from '../../../style/zIndex';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { NotificationItem } from './NotificationItem';

const styles = StyleSheet.create({
  notificationList: {
    position: 'absolute',
    top: 12,
    right: 20,
    maxWidth: 500,
    zIndex: zIndex.notification,
  },
  notificationItemWrapper: {
    marginTop: 12,
  },
});

export namespace NotificationList {
  export interface Props {}
}

/**
 * This component renders a list of toast notifications. There are two ways that a notification can
 * be removed:
 *  1) Notifications are automatically removed after a certain period of time
 *  2) User can manually remove the notification before the auto-removal occurs by clicking an icon
 *
 * The NotificationList that we export is wrapped in <ProvideTimers/> to ensure that all setTimeouts are
 * cleaned up when the component unmounts, as well as to maintain consistency with other Blend codebases.
 *
 * TODO (RON-257) Some changes may be necessary once we know how the backend for notifications will work
 * (as well as how we'll be consuming them in the frontend), such as:
 * - Make sure notifications can be dynamically added to the NotificationList without re-creating
 *   auto-remove timeouts for existing notifications
 */
export const NotificationList: React.FC<NotificationList.Props> = () => {
  const { notifications } = useRootSelector(state => state.page);
  if (notifications?.length > 0) {
    return (
      <div className={css(styles.notificationList)}>
        {notifications.map(notification => {
          return (
            <div key={notification.id} className={css(styles.notificationItemWrapper)}>
              <NotificationItem
                id={notification.id}
                type={notification.type}
                content={notification.content}
                shouldAutoRemove={notification.shouldAutoRemove}
                iconType={notification.iconType}
                action={notification.action}
                handleActionClick={notification.handleActionClick}
                disallowDismiss={notification.disallowDismiss}
              />
            </div>
          );
        })}
      </div>
    );
  } else {
    return null;
  }
};
